import { Center, Flex, FormLabel, Heading, Input, Button, ButtonGroup, Skeleton } from "@chakra-ui/react";
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonBack, Error, Layout } from "../../../components";
import { RootState } from "../../app/mainReducer";
import { CargaFinalizada, CargaItem, EntregaCardList } from "../../carga/components";
import type { Entrega } from "../../carga/utils/agruparEntregas";
import config from "../../../utils/config";
import { agrupamentoActions } from "../reducer";
import { Carga, CargaItem as CargaItemType } from "../../carga/types";
import { getOrdemEntrega } from "../../carga/types/carga_item";
import { cargaActions } from "../../carga/reducer";

const AgrupamentoItemList = () => {
  const isLoading = useSelector((state: RootState) => state.agrupamento.isLoading);
  const cargas = useSelector((state: RootState) => state.carga.cargas);
  const error = useSelector((state: RootState) => state.agrupamento.error || '');
  const networkOnline = useSelector((state: RootState) => state.login.networkOnline || false);

  const { ukey } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cargasDoAgrupamento = (cargas ?? []).filter((carga) => carga.n01_ukey == ukey);

  const gestao_entrega_pendentes = useSelector((state: RootState) => state.entrega.gestao_entrega_pendentes);

  useEffect(() => {
    if (!ukey) return;

    const cargaEncontrada = cargasDoAgrupamento.length > 0;

    if (cargaEncontrada) {
      return;
    }

    if (gestao_entrega_pendentes.length) {
      return;
    }

    if (networkOnline) {
      dispatch(agrupamentoActions.agrupamentoRequest(ukey));
    } else {
      dispatch(agrupamentoActions.agrupamentosError('Não é possível buscar as entregas, você está sem internet.'));
    }
  }, [dispatch, agrupamentoActions, cargas, ukey])

  const search = () => {
    if (networkOnline) {
      if (!ukey) return;

      dispatch(agrupamentoActions.agrupamentoRequest(ukey));
    }
  }
  const [filtro, setFiltro] = useState("");
  const [ordenarPor, setOrdenarPor] = useState<"entrega" | "nota">("entrega");

  const itensDoAgrupamento = (): CargaItemType[] => {
    const itens: CargaItemType[] = [];
    for (const carga of cargasDoAgrupamento) {
      for (const item of carga.itens) {
        itens.push(item);
      }
    }
    return itens;
  };

  const getItensFiltrados = (): CargaItemType[] => {
    if (!itensDoAgrupamento().length) return [];

    if (!filtro) return itensDoAgrupamento();

    return itensDoAgrupamento().filter((i) => {
      return i.cliente_fantasia.toLowerCase().includes(filtro.toLowerCase()) ||
        i.cliente_razao_social.toLowerCase().includes(filtro.toLowerCase()) ||
        i.nota_numero.toLowerCase().includes(filtro.toLowerCase());
    });
  };

  const itensFiltrados = getItensFiltrados();
  const hasOrdemEntrega = itensFiltrados.some((i) => getOrdemEntrega(i) > 0);

  const getItens = (): CargaItemType[] => {
    const itens = [...itensFiltrados];
    if (hasOrdemEntrega && ordenarPor === "entrega") {
      itens.sort((a, b) => {
        const ordA = getOrdemEntrega(a);
        const ordB = getOrdemEntrega(b);
        if (ordA !== ordB) return ordA - ordB;
        return (a.nota_numero || "").localeCompare(b.nota_numero || "");
      });
    } else {
      itens.sort((a, b) => (a.nota_numero || "").localeCompare(b.nota_numero || ""));
    }
    return itens;
  };

  const selecionaCargaERedireciona = (cargaItem: CargaItemType) => {
    dispatch(cargaActions.cargaRequestSuccess(cargasDoAgrupamento.find((carga) => carga.j14_ukey == cargaItem.j14_ukey) as Carga))

    navigate(`/entrega/nota/${cargaItem.j14_ukey}/${cargaItem.j10_ukey}/opcoes`)
  }

  const selecionaCargaERedirecionaEntrega = (entrega: Entrega) => {
    dispatch(cargaActions.cargaRequestSuccess(cargasDoAgrupamento.find((carga) => carga.j14_ukey == entrega.j14_ukey) as Carga))

    navigate(`/entrega/grupo/${entrega.j14_ukey}/${entrega.j10_ukey_rota}/opcoes`)
  }

  return (
    <Layout showFooter={true} backTo="/cargas" isLoading={isLoading} onSearch={search} title={<ButtonBack to="/cargas" />}>
      <>
        <Center mt={1} mb={2} textAlign={'center'}>
          <Heading>{config.listarPorEntrega ? "Entregas" : "Notas"} do agrupamento {(cargasDoAgrupamento ?? [])[0]?.agrupamento_numero}</Heading>
        </Center>

        <Error error={error} />
        {hasOrdemEntrega && (
          <Flex width="full" direction="column" align="center" mb={2} px={2}>
            <FormLabel>Ordenar por:</FormLabel>
            <ButtonGroup isAttached size="sm" variant="outline">
              <Button
                colorScheme="teal"
                variant={ordenarPor === "entrega" ? "solid" : "outline"}
                onClick={() => setOrdenarPor("entrega")}
              >
                Entrega
              </Button>
              <Button
                colorScheme="teal"
                variant={ordenarPor === "nota" ? "solid" : "outline"}
                onClick={() => setOrdenarPor("nota")}
              >
                Nota Fiscal
              </Button>
            </ButtonGroup>
          </Flex>
        )}
        {(getItens().length > 0 || (getItens().length === 0 && filtro)) &&
          <Flex width="full" wrap="wrap">
            <FormLabel mx={2}>Filtrar pelo número da Nota ou Cliente</FormLabel>
            <Input mx={2} mb={4} bg="white" placeholder="Digite o número da nota ou nome do cliente" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
          </Flex>
        }
        {
          isLoading && [1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton height='90px' mb={1} key={i} />
          ))
        }
        {
          !isLoading && config.listarPorEntrega &&
          <EntregaCardList
            itens={itensDoAgrupamento()}
            filtro={filtro}
            ordenarPor={hasOrdemEntrega ? ordenarPor : "nota"}
            onEntregar={selecionaCargaERedirecionaEntrega}
          />
        }
        {
          !isLoading && !config.listarPorEntrega && getItens().map((carga_item) => (
            <CargaItem onClick={() => selecionaCargaERedireciona(carga_item)} carga_item={carga_item} key={carga_item.nota_numero} />
          ))
        }

        {getItens().length === 0 && !isLoading && !filtro &&
          <CargaFinalizada />
        }
      </>
    </Layout>
  )
}

export default AgrupamentoItemList;